import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { BlogPostSummary, BlogPillar } from "~/lib/blog-queries";
import { breadcrumbSchema, schemaGraph, blogSchema } from "~/lib/seo/schema";
import { buildMeta } from "~/lib/seo/meta";
import { JsonLd } from "~/components/seo/JsonLd";
import { serializeForServer } from "~/lib/server-serialization";
import { getLocale } from "~/paraglide/runtime";
import { BlogPostLink, LocalizedBlogLink } from "~/components/blog/BlogLink";
import { getBlogCanonical, getBlogHeadLinks } from "~/lib/blog-seo";

// ── Types ───────────────────────────────────────────────────────────────────

interface PillarData extends BlogPillar {
  posts: BlogPostSummary[];
}

// ── Server loader ───────────────────────────────────────────────────────────

const fetchPillar = createServerFn({ method: "GET" })
  .validator(z.string())
  .handler(async ({ data: slug }) => {
    const { getPillarWithPosts } = await import("~/lib/blog-queries");
    return serializeForServer(await getPillarWithPosts(slug, getLocale()));
  });

// ── Route ───────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/blog/pillar/$slug")({
  loader: ({ params }) => fetchPillar({ data: params.slug }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const pillar = loaderData as PillarData;
    const seoPage = { locale: pillar.locale, pageType: "pillar", slug: pillar.slug };
    return {
      meta: buildMeta({
        title: `${pillar.title} — Blog | Alchema`,
        description: pillar.description ?? `Articles about ${pillar.title.toLowerCase()} for EU job seekers.`,
        url: getBlogCanonical(seoPage).path,
      }),
      links: getBlogHeadLinks(seoPage),
    };
  },
  component: PillarPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center" style={{ backgroundColor: "var(--bg)" }}>
      <h1 className="text-2xl font-semibold" style={{ color: "var(--text)" }}>Topic not found</h1>
      <LocalizedBlogLink
        href="/blog"
        className="mt-6 inline-flex rounded-[10px] px-6 py-2.5 text-sm font-medium"
        style={{ background: "var(--accent)", color: "var(--text-inverse)" }}
      >
        View all articles
      </LocalizedBlogLink>
    </div>
  ),
});

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(date: Date | string | null): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// ── Page ────────────────────────────────────────────────────────────────────

function PillarPage() {
  const pillar = Route.useLoaderData();
  if (!pillar) throw notFound();

  const jsonLd = schemaGraph(
    blogSchema(pillar.locale),
    breadcrumbSchema([
      { name: "Home", url: pillar.locale === "en" ? "/" : `/${pillar.locale}` },
      { name: "Blog", url: getBlogCanonical({ locale: pillar.locale, pageType: "index" }).path },
      { name: pillar.title, url: getBlogCanonical({ locale: pillar.locale, pageType: "pillar", slug: pillar.slug }).path },
    ]),
  );

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={jsonLd} />

      {/* Header */}
      <section className="mx-auto max-w-3xl px-6 pt-24 pb-8">
        <nav
          aria-label="Breadcrumb"
          className="mb-8 text-[0.8125rem]"
          style={{ color: "var(--text-muted)" }}
        >
          <ol className="flex items-center gap-1.5">
            <li>
              <LocalizedBlogLink href="/blog" className="transition-colors duration-150 hover:text-[var(--text)]">
                Blog
              </LocalizedBlogLink>
            </li>
            <li aria-hidden="true" className="opacity-40">/</li>
            <li style={{ color: "var(--text-secondary)" }}>{pillar.title}</li>
          </ol>
        </nav>

        <h1
          className="text-[clamp(2rem,5vw,3rem)] font-bold leading-[1.1] tracking-[-0.025em]"
          style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
        >
          {pillar.title}
        </h1>

        {pillar.description && (
          <p
            className="mt-4 text-[clamp(1rem,2vw,1.125rem)] leading-relaxed"
            style={{ color: "var(--text-secondary)", maxWidth: "52ch" }}
          >
            {pillar.description}
          </p>
        )}

        <p
          className="mt-3 text-[0.8125rem]"
          style={{ color: "var(--text-muted)" }}
        >
          {pillar.posts.length} article{pillar.posts.length !== 1 ? "s" : ""}
        </p>
      </section>

      {/* Post list */}
      <section className="mx-auto max-w-3xl px-6 pb-32">
        {pillar.posts.length === 0 ? (
          <p className="py-16 text-center text-sm" style={{ color: "var(--text-muted)" }}>
            No articles in this topic yet.
          </p>
        ) : (
          pillar.posts.map((post: BlogPostSummary, i: number) => (
            <BlogPostLink
              key={`${post.locale}:${post.slug}`}
              post={post}
              className="group block"
            >
              <article
                className="py-7"
                style={{ borderBottom: "1px solid var(--border-subtle)" }}
              >
                <div
                  className="flex items-center gap-2 text-[0.75rem] tracking-wide uppercase"
                  style={{ color: "var(--text-muted)" }}
                >
                  <time dateTime={post.publishedAt?.toString()}>
                    {formatDate(post.publishedAt)}
                  </time>
                  {post.readingTimeMin && (
                    <>
                      <span aria-hidden="true" className="opacity-40">/</span>
                      <span>{post.readingTimeMin} min</span>
                    </>
                  )}
                </div>

                <h2
                  className="mt-3 text-[clamp(1.125rem,2vw,1.375rem)] font-semibold leading-tight tracking-[-0.01em] transition-colors duration-150 group-hover:text-[var(--accent)]"
                  style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
                >
                  {post.title}
                </h2>

                {post.excerpt && (
                  <p
                    className="mt-2 text-[0.9375rem] leading-relaxed"
                    style={{ color: "var(--text-secondary)", maxWidth: "60ch" }}
                  >
                    {post.excerpt}
                  </p>
                )}

                {post.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {post.tags.slice(0, 3).map((tag: string) => (
                      <span
                        key={tag}
                        className="rounded-full px-2.5 py-0.5 text-[0.6875rem] font-medium"
                        style={{ background: "var(--surface-raised)", color: "var(--text-muted)" }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            </BlogPostLink>
          ))
        )}
      </section>
    </div>
  );
}
